import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { trackButtonClick } from '../services/telemetry';

type ShowConfirm = (
  title: string,
  message: string,
  options?: {
    confirmText?: string;
    cancelText?: string;
    confirmVariant?: 'primary' | 'danger' | 'primaryDark';
  }
) => Promise<boolean>;

/**
 * Returns a click handler that blocks downloads for guests behind a
 * "sign up to unlock" prompt.
 *
 * Downloads are the one project-page action that can't simply be no-op'd for
 * guests: the PDF export runs entirely client-side (jspdf), so it would
 * otherwise "work" without an account. We instead intercept the click, show a
 * confirm dialog, and route to `/sign-up` if the guest opts in. Their current
 * result is untouched and still on screen.
 */
export function useGuestDownloadLock(showConfirm: ShowConfirm) {
  const navigate = useNavigate();

  return useCallback(async () => {
    trackButtonClick('GuestProjectPage', 'Download Locked Prompt');
    const proceed = await showConfirm(
      'Sign up to unlock downloads',
      'Downloading your score with fingerings is a free account feature. Sign up to unlock.',
      {
        confirmText: 'Sign up',
        cancelText: 'Not now',
        confirmVariant: 'primaryDark',
      }
    );
    if (proceed) {
      navigate('/sign-up');
    }
  }, [showConfirm, navigate]);
}
